import type { SSRModalDropDownSelectionProps } from '@fz/ui-components-react';
import type {
  MealAndAssistData,
  AssistPassengerDetails,
  AssistQuoteWithPassengerDetails,
} from '@models/landing/api/baggage.model';
import { PassengerTypeCode, SsrType } from '@models/common/shared-enums.model';
import type { Passenger } from '@models/api/extras-response.model';
import { getResource } from '@utils/resource-translator.util';
import {
  formatPassengerDisplayName,
  isInfantPassengerTypeLabel,
  resolvePassengerTypeLabel,
  resolveDefaultSelectablePassengerId,
  sortPassengersWithAssignedInfants,
} from '@shared/components/ssr-passenger.util';
import { i18n } from '@i18n/i18n.service';

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_CONFIRM_LABEL = 'Confirm.Text';
const DEFAULT_TITLE = 'Assist.text';
const DEFAULT_CONTENT_TITLE = 'Extras.MeetAndAssist.ContentTitle.Text';
const DEFAULT_INFO_PANEL_TEXT = 'Extras.MeetAndAssist.InfoPanel.Text';
const DEFAULT_SLOT_PLACEHOLDER = 'Extra.Assist.Dropdownplaceholder.Text';
const DEFAULT_CHANGE_LABEL = 'Common.Change.Text';
const DEFAULT_TRIGGER_ARIA_LABEL = 'Extras.MeetAndAssist.ChangeTimeSlot.AriaLabel.Text';
const DEFAULT_NO_SLOTS_TEXT = 'Extras.MeetAndAssist.NoSlots.Text';
const DEFAULT_UNAVAILABLE_TITLE = 'Extras.MeetAndAssist.Unavailable.Title.Text';
const DEFAULT_UNAVAILABLE_DESCRIPTION = 'Extras.MeetAndAssist.Unavailable.Description.Text';
const INFANT_ASSIST_TEXT_KEY = 'Extras.Assist.Infant.Text';

// ============================================================================
// PUBLIC TYPES
// ============================================================================

export interface AssistAddApiPayload {
  [key: string]: unknown;
  ssrType: SsrType.ASSIST;
  logicalFlightId: string;
  physicalFlightId: string;
  paxJourneyId: string;
  resPaxId: number;
  recordNumber: number;
  codeType: string;
  subCodeType: string;
  isBusiness: boolean;
  secureHash: string;
  assistSlotFrom: string;
  assistSlotTo: string;
  isAssistOpted: boolean;
}

export type AssistModalPassenger = SSRModalDropDownSelectionProps['passengers'][number];

export type AssistModalTabItem = NonNullable<
  SSRModalDropDownSelectionProps['flightTabItems']
>[number];

export type AssistDropDownOption =
  SSRModalDropDownSelectionProps['groups'][number]['options'][number];

export type AssistModalGroup = SSRModalDropDownSelectionProps['groups'][number];

export interface AssistModalOptionInfo {
  id: string;
  slotFrom: string;
  slotTo: string;
  label: string;
  codeType: string;
  subCodeType: string;
  isBusiness: boolean;
  amount: string;
  quantityAvailable: number;
  chargeComment: string;
  secureHash: string;
  logicalFlightId: string;
  physicalFlightId: string;
  ssrType: SsrType.ASSIST;
  passengerDetails: AssistPassengerDetails[];
}

export interface AssistModalViewModel {
  title: SSRModalDropDownSelectionProps['title'];
  confirmButtonLabel: SSRModalDropDownSelectionProps['confirmButtonLabel'];
  currencyCode: SSRModalDropDownSelectionProps['currencyCode'];
  contentTitle: SSRModalDropDownSelectionProps['contentTitle'];
  contentDescription?: SSRModalDropDownSelectionProps['contentDescription'];
  infoPanelText: SSRModalDropDownSelectionProps['infoPanelText'];
  topContent: SSRModalDropDownSelectionProps['topContent'];
  passengers: SSRModalDropDownSelectionProps['passengers'];
  passengerSSRDetails: NonNullable<SSRModalDropDownSelectionProps['passengerSSRDetails']>;
  flightTabItems: NonNullable<SSRModalDropDownSelectionProps['flightTabItems']>;
  groups: SSRModalDropDownSelectionProps['groups'];
  isUnavailable: SSRModalDropDownSelectionProps['isUnavailable'];
  unavailableTitle: SSRModalDropDownSelectionProps['unavailableTitle'];
  unavailableDescription: SSRModalDropDownSelectionProps['unavailableDescription'];
  unavailableVariant: SSRModalDropDownSelectionProps['unavailableVariant'];
  /** Full option info keyed by logicalFlightId — used internally by the component for payload building */
  optionInfoByFlightId: Record<string, AssistModalOptionInfo[]>;
  initialSelectedPassengerId: string;
  initialActiveFlightTabValue: string;
  initialToggleState: 'YES' | 'NO';
}

const ASSIST_SSR_TYPE = String(SsrType.ASSIST);
const INFANT_PASSENGER_TYPE = Number(PassengerTypeCode.Infant);

// ============================================================================
// PRIVATE HELPERS
// ============================================================================

const resolveTranslatedText = (resourceKey: string, fallback = ''): string => {
  const translated = getResource(resourceKey, undefined, '');
  return translated && translated !== resourceKey ? translated : fallback;
};

const parseSlotDate = (dateStr: string): Date => {
  // Handle "d/M/yyyy HH:mm:ss" format returned by the API
  // Check regex first to avoid misinterpretation by Date constructor (which assumes MM/DD/YYYY)
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})$/.exec(dateStr);
  if (match) {
    const [, day, month, year, hours, minutes, seconds] = match;
    // API returns d/M/yyyy format (day first, then month)
    // Date constructor expects: new Date(year, monthIndex, day, ...)
    return new Date(
      Number(year),
      Number(month) - 1, // Month is 0-indexed in JavaScript Date
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds),
    );
  }

  // Fallback to ISO parsing if regex doesn't match
  const iso = new Date(dateStr);
  if (!Number.isNaN(iso.getTime())) {
    return iso;
  }

  return new Date(NaN);
};

const formatTimeSlotLabel = (slotFrom: string, slotTo: string): string => {
  const fromDate = parseSlotDate(slotFrom);
  const toDate = parseSlotDate(slotTo);

  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return `${slotFrom} - ${slotTo}`;
  }

  const PAD_LENGTH = 2;
  const PAD_CHAR = '0';

  // Use current language for month name translation, but keep numbers in Western Arabic numerals
  const locale = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  // Get translated month name
  const monthName = fromDate.toLocaleDateString(locale, { month: 'long' });

  // Use standard numerals for day, year, and time (not locale-specific)
  const day = fromDate.getDate();
  const year = fromDate.getFullYear();
  const fromHours = fromDate.getHours();
  const fromMinutes = fromDate.getMinutes().toString().padStart(PAD_LENGTH, PAD_CHAR);
  const toHours = toDate.getHours();
  const toMinutes = toDate.getMinutes().toString().padStart(PAD_LENGTH, PAD_CHAR);

  return `${day} ${monthName} ${year} | ${fromHours}:${fromMinutes} - ${toHours}:${toMinutes}`;
};

const formatTimeRangeOnly = (slotFrom: string, slotTo: string): string => {
  const fromDate = parseSlotDate(slotFrom);
  const toDate = parseSlotDate(slotTo);

  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return `${slotFrom} - ${slotTo}`;
  }

  const PAD_LENGTH = 2;
  const PAD_CHAR = '0';

  // Use standard numerals for time (not locale-specific)
  const fromHours = fromDate.getHours();
  const fromMinutes = fromDate.getMinutes().toString().padStart(PAD_LENGTH, PAD_CHAR);
  const toHours = toDate.getHours();
  const toMinutes = toDate.getMinutes().toString().padStart(PAD_LENGTH, PAD_CHAR);

  return `${fromHours}:${fromMinutes} - ${toHours}:${toMinutes}`;
};

export const resolveAssistSlotDisplayLabel = (option: AssistModalOptionInfo): string => {
  const formattedLabel = formatTimeSlotLabel(option.slotFrom, option.slotTo);
  return formattedLabel || option.label;
};

export const resolveAssistPassengerSlotLabel = (option: AssistModalOptionInfo): string => {
  return formatTimeRangeOnly(option.slotFrom, option.slotTo);
};

const resolveDisplayPassengerMap = (
  mealAndAssist?: MealAndAssistData,
): Record<number, { seatNumber: string; pnrNumber: string }> => {
  if (!mealAndAssist?.assistQuotes || mealAndAssist.assistQuotes.length === 0) {
    return {};
  }

  return mealAndAssist.assistQuotes.reduce<
    Record<number, { seatNumber: string; pnrNumber: string }>
  >((acc, quote) => {
    quote.assistInfo.forEach((info) => {
      info.passengerDetails.forEach((detail) => {
        if (!acc[detail.resPaxId]) {
          acc[detail.resPaxId] = {
            seatNumber: detail.seatNumber ?? '',
            pnrNumber: detail.pnrNumber ?? '',
          };
        }
      });
    });
    return acc;
  }, {});
};

const mapPassengers = (
  mealAndAssist?: MealAndAssistData,
  passengerList?: Passenger[],
): AssistModalPassenger[] => {
  const displayPassengerMap = resolveDisplayPassengerMap(mealAndAssist);

  if (passengerList && passengerList.length > 0) {
    return sortPassengersWithAssignedInfants(passengerList).map((passenger) => ({
      id: passenger.resPaxId.toString(),
      name: formatPassengerDisplayName(passenger.title, passenger.firstName, passenger.lastName),
      type: resolvePassengerTypeLabel(passenger.passengerType),
      isInfant: Number(passenger.passengerType) === INFANT_PASSENGER_TYPE,
      seatNumber: displayPassengerMap[passenger.resPaxId]?.seatNumber ?? '',
      pnrNumber: displayPassengerMap[passenger.resPaxId]?.pnrNumber,
    }));
  }

  // Fallback: derive unique passengers from assistInfo passengerDetails
  const passengersMap = new Map<number, { name: string; seatNumber: string; pnrNumber?: string }>();

  mealAndAssist?.assistQuotes.forEach((quote) => {
    quote.assistInfo.forEach((info) => {
      info.passengerDetails.forEach((detail) => {
        if (!passengersMap.has(detail.resPaxId)) {
          passengersMap.set(detail.resPaxId, {
            name: formatPassengerDisplayName(detail.title, detail.firstName, detail.lastName),
            seatNumber: detail.seatNumber ?? '',
            pnrNumber: detail.pnrNumber,
          });
        }
      });
    });
  });

  return Array.from(passengersMap.entries()).map(([resPaxId, data]) => ({
    id: resPaxId.toString(),
    name: data.name,
    type: '',
    isInfant: false,
    seatNumber: data.seatNumber,
    pnrNumber: data.pnrNumber,
  }));
};

const resolveUniqueQuotes = (
  mealAndAssist?: MealAndAssistData,
): AssistQuoteWithPassengerDetails[] => {
  return (mealAndAssist?.assistQuotes ?? [])
    .filter(
      (quote, index, quotes) =>
        quotes.findIndex((q) => q.logicalFlightId === quote.logicalFlightId) === index,
    )
    .filter(
      // Only include flights that have at least one assist slot with applicable pax
      (quote) =>
        quote.assistInfo &&
        quote.assistInfo.some((info) => info.applicablePax && info.applicablePax.length > 0),
    );
};

const mapFlightTabItems = (mealAndAssist?: MealAndAssistData): AssistModalTabItem[] => {
  const assistFlightTabItems = mealAndAssist?.assistFlightTabItems ?? [];
  const uniqueQuotes = resolveUniqueQuotes(mealAndAssist);

  if (assistFlightTabItems.length > 0) {
    return assistFlightTabItems.map((tabItem, index) => ({
      value: tabItem.logicalFlightID ?? uniqueQuotes[index]?.logicalFlightId ?? tabItem.value,
      label: tabItem.label,
      content: tabItem.content,
    }));
  }

  return uniqueQuotes.map((quote, index) => ({
    value: quote.logicalFlightId,
    label: index === 0 ? 'Departure' : 'Return',
    content: quote.flightNumber ? `Flight ${quote.flightNumber}` : quote.logicalFlightId,
  }));
};

const buildOptionInfoForQuote = (
  quote: AssistQuoteWithPassengerDetails,
): AssistModalOptionInfo[] => {
  // Filter out slots where no applicable passengers are available
  return quote.assistInfo
    .filter((info) => info.applicablePax && info.applicablePax.length > 0)
    .map((info, index) => ({
      id: `assist-slot-${quote.logicalFlightId}-${index}`,
      slotFrom: info.assistSlotFrom,
      slotTo: info.assistSlotTo,
      label: formatTimeSlotLabel(info.assistSlotFrom, info.assistSlotTo),
      codeType: info.codeType,
      subCodeType: info.subCodeType,
      isBusiness: info.isBusiness,
      amount: info.amount ?? '0',
      quantityAvailable: info.quantityAvailable,
      chargeComment: info.chargeComment ?? '',
      secureHash: info.secureHash,
      logicalFlightId: quote.logicalFlightId,
      physicalFlightId: quote.physicalFlightId,
      ssrType: SsrType.ASSIST,
      passengerDetails: info.passengerDetails,
    }));
};

const mapOptionInfoByFlightId = (
  mealAndAssist?: MealAndAssistData,
): Record<string, AssistModalOptionInfo[]> => {
  return resolveUniqueQuotes(mealAndAssist).reduce<Record<string, AssistModalOptionInfo[]>>(
    (acc, quote) => {
      acc[quote.logicalFlightId] = buildOptionInfoForQuote(quote);
      return acc;
    },
    {},
  );
};

const mapPassengerSSRDetails = (
  passengers: AssistModalPassenger[],
): Record<string, { subText1?: string; subText2?: string }> => {
  const resolvedInfantSubText = resolveTranslatedText(
    INFANT_ASSIST_TEXT_KEY,
    INFANT_ASSIST_TEXT_KEY,
  );

  return passengers.reduce<Record<string, { subText1?: string; subText2?: string }>>(
    (acc, passenger) => {
      acc[passenger.id] = isInfantPassengerTypeLabel(passenger.type)
        ? { subText1: resolvedInfantSubText, subText2: '' }
        : { subText1: '', subText2: '' };
      return acc;
    },
    {},
  );
};

export const buildGroupsForFlightId = (
  optionInfoByFlightId: Record<string, AssistModalOptionInfo[]>,
  flightId: string,
  selectedOptionId?: string,
): AssistModalGroup[] => {
  const optionInfoList = optionInfoByFlightId[flightId] ?? [];
  if (optionInfoList.length === 0) return [];

  const resolvedPlaceholder = resolveTranslatedText(
    DEFAULT_SLOT_PLACEHOLDER,
    'Select the time slot',
  );
  const resolvedChangeLabel = resolveTranslatedText(DEFAULT_CHANGE_LABEL, 'Change');
  const resolvedTriggerAriaLabel = resolveTranslatedText(
    DEFAULT_TRIGGER_ARIA_LABEL,
    'Change time slot',
  );
  const resolvedNoSlotsText = resolveTranslatedText(
    DEFAULT_NO_SLOTS_TEXT,
    'No time slots available',
  );

  return [
    {
      id: 'time-slot',
      selectedOptionId,
      placeholder: resolvedPlaceholder,
      changeLabel: resolvedChangeLabel,
      triggerAriaLabel: resolvedTriggerAriaLabel,
      options: optionInfoList.map((info) => ({
        id: info.id,
        label: info.label,
      })),
      emptyOptionsText: resolvedNoSlotsText,
    },
  ];
};

// ============================================================================
// PUBLIC MAPPER
// ============================================================================

// eslint-disable-next-line max-lines-per-function
export const mapAssistToModalView = (
  mealAndAssist?: MealAndAssistData,
  passengerList?: Passenger[],
): AssistModalViewModel => {
  const passengers = mapPassengers(mealAndAssist, passengerList);
  const flightTabItems = mapFlightTabItems(mealAndAssist);
  const optionInfoByFlightId = mapOptionInfoByFlightId(mealAndAssist);
  const initialSelectedPassengerId = resolveDefaultSelectablePassengerId(passengers);
  const initialActiveFlightTabValue = flightTabItems[0]?.value ?? '';
  const groups = buildGroupsForFlightId(optionInfoByFlightId, initialActiveFlightTabValue);
  const hasSlots = Object.values(optionInfoByFlightId).some((infos) => infos.length > 0);

  // Resolve initial toggle state from the first passenger's includedExtrasList ASSIST entry
  const resolvedInitialToggleState = ((): 'YES' | 'NO' => {
    if (!passengerList || passengerList.length === 0) return 'YES';
    const firstPassenger = passengerList[0];
    const flightDetail = firstPassenger.flightsDetail?.find(
      (fd) => fd.logicalFlightId === initialActiveFlightTabValue,
    );
    const includedAssist = flightDetail?.includedExtrasList?.find(
      (extra) => String(extra.ssrType) === ASSIST_SSR_TYPE,
    );
    if (includedAssist) {
      return includedAssist.isAssistOpted ? 'YES' : 'NO';
    }
    return 'YES';
  })();

  const resolvedTitle = resolveTranslatedText(DEFAULT_TITLE, 'Assist.text');
  const resolvedConfirmLabel = resolveTranslatedText(DEFAULT_CONFIRM_LABEL, 'Confirm');
  const resolvedContentTitle = resolveTranslatedText(
    getResource(DEFAULT_CONTENT_TITLE),
    getResource('Extras.MeetAndAssist.ContentTitle.Text'),
  );
  const resolvedInfoPanelText = resolveTranslatedText(
    getResource(DEFAULT_INFO_PANEL_TEXT),
    getResource('Extra.Assist.Subheading.Text'),
  );
  const resolvedUnavailableTitle = resolveTranslatedText(
    DEFAULT_UNAVAILABLE_TITLE,
    'Meet and Assist is unavailable',
  );
  const resolvedUnavailableDescription = resolveTranslatedText(
    DEFAULT_UNAVAILABLE_DESCRIPTION,
    'No time slots available for this flight.',
  );

  const currencyCode = mealAndAssist?.assistQuotes.find((q) => q.currency)?.currency ?? '';

  return {
    title: resolvedTitle,
    confirmButtonLabel: resolvedConfirmLabel,
    currencyCode,
    contentTitle: resolvedContentTitle,
    // contentDescription: resolvedContentDescription,
    infoPanelText: resolvedInfoPanelText,
    topContent: undefined,
    passengers,
    passengerSSRDetails: mapPassengerSSRDetails(passengers),
    flightTabItems,
    groups,
    optionInfoByFlightId,
    initialSelectedPassengerId,
    initialActiveFlightTabValue,
    initialToggleState: resolvedInitialToggleState,
    isUnavailable: !hasSlots,
    unavailableTitle: !hasSlots ? resolvedUnavailableTitle : undefined,
    unavailableDescription: !hasSlots ? resolvedUnavailableDescription : undefined,
    unavailableVariant: !hasSlots ? 'panel' : undefined,
  };
};
